import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form'
import SydLabel, { hookFormErrorAdapter } from '../../../../../commonDesign/SydLabel'
import SydInput from '../../../../../commonDesign/SydInput'
import SydSelect from '../../../../../commonDesign/SydSelect'
import SydButton from '../../../../../commonDesign/Button'
import {
  buildSupplementalDisplayConfigPayload,
  DETAIL_FIELD_FORMAT_OPTIONS,
  detailFieldsToRows,
  formatMatchValueForForm,
  includeCurrentSelectValue,
  parseMatchValueForSubmit,
  SUPPLEMENTAL_FIELD_MAP_KEYS,
  SUPPLEMENTAL_LAYOUT_OPTIONS,
  SUPPLEMENTAL_MATCH_OP_OPTIONS
} from '../SupplementalDataTab/supplementalFormShared'
import { DEFAULT_SUPPLEMENTAL_ITEMS_PATH } from '../SupplementalDataTab/supplementalPipelineVariablesForm'
import { useDialogStyles } from './common'

function buildDisplayConfigPayload (d, baseConfig) {
  return buildSupplementalDisplayConfigPayload(d, { includeItemsPath: true, baseConfig })
}

function SupplementalSourceForm ({ templateItem, mapping, onCancel, onComplete }) {
  const classes = useDialogStyles()
  const form = useForm({
    mode: 'onChange',
    defaultValues: {
      supplementalMappingId: mapping?.supplementalMappingId,
      displayConfig: {
        layout: mapping?.displayConfig?.layout ?? 'tasksAccordion',
        itemsPath: mapping?.displayConfig?.itemsPath ?? DEFAULT_SUPPLEMENTAL_ITEMS_PATH,
        header: mapping?.displayConfig?.header ?? '',
        detailFieldRows: detailFieldsToRows(mapping?.displayConfig?.detailFields),
        completedValue: mapping?.displayConfig?.completedValue ?? '',
        fields: {
          label: mapping?.displayConfig?.fields?.label ?? '',
          status: mapping?.displayConfig?.fields?.status ?? '',
          type: mapping?.displayConfig?.fields?.type ?? ''
        },
        blueprintCode: mapping?.displayConfig?.blueprintCode ?? '',
        presentationKey: mapping?.displayConfig?.presentationKey ?? '',
        idField: mapping?.displayConfig?.idField ?? '',
        responseItemsPath: mapping?.displayConfig?.responseItemsPath ?? '',
        containerStyle: mapping?.displayConfig?.containerStyle ? JSON.stringify(mapping.displayConfig.containerStyle) : ''
      },
      matchConfig: {
        field: mapping?.matchConfig?.field ?? 'codeName',
        op: mapping?.matchConfig?.op ?? 'eq',
        value: formatMatchValueForForm(mapping?.matchConfig?.value)
      },
      ordinal: mapping?.ordinal ?? 0,
      enabled: mapping?.enabled ?? true
    }
  })

  const { fields: detailFieldRows, append: appendDetailField, remove: removeDetailField } = useFieldArray({
    control: form.control,
    name: 'displayConfig.detailFieldRows'
  })
  const layoutOptions = useMemo(
    () => includeCurrentSelectValue(SUPPLEMENTAL_LAYOUT_OPTIONS, mapping?.displayConfig?.layout),
    [mapping]
  )
  const displayLayout = useWatch({ control: form.control, name: 'displayConfig.layout', defaultValue: 'tasksAccordion' })
  const isSmartComponent = displayLayout === 'smartComponent'
  const matchOp = useWatch({ control: form.control, name: 'matchConfig.op', defaultValue: 'eq' })
  const [processing, setProcessing] = useState(false)
  const onSubmit = useCallback(async (data) => {
    setProcessing(true)
    try {
      const displayConfig = buildDisplayConfigPayload(data.displayConfig, mapping?.displayConfig)
      if (!displayConfig) {
        form.setError('displayConfig.layout', { type: 'validate', message: 'Choose a layout' })
        return
      }
      const mf = data.matchConfig?.field?.trim() ?? ''
      if (!mf) {
        form.setError('matchConfig.field', { type: 'validate', message: 'Match field is required' })
        return
      }
      const parsedValue = parseMatchValueForSubmit(data.matchConfig?.value, data.matchConfig?.op, form)
      if (parsedValue === undefined) return
      await onComplete({
        supplementalMappingId: data.supplementalMappingId,
        statusTemplateItemId: templateItem?.statusTemplateItemId,
        matchConfig: {
          field: mf,
          op: data.matchConfig.op,
          value: parsedValue
        },
        displayConfig,
        ordinal: data.ordinal,
        enabled: data.enabled
      })
    } finally {
      setProcessing(false)
    }
  }, [templateItem, onComplete, form, mapping])

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className={classes.form}>
      <p className='font-size-sm text-secondary'>
        Match rules map pipeline rows to this line (field on each row, value may be <code className='font-size-sm'>$codeName</code> or <code className='font-size-sm'>$statusReportItemId</code>). For In / Not in, use a JSON array or comma-separated list.
      </p>
      <Controller
        name='displayConfig.layout'
        control={form.control}
        render={({ field }) => (
          <SydLabel label='Display layout'>
            <SydSelect {...field} options={layoutOptions} size='sm' />
          </SydLabel>
        )}
      />
      {!isSmartComponent && (
        <Controller
          name='displayConfig.itemsPath'
          control={form.control}
          render={({ field }) => (
            <SydLabel
              label='Items path (optional)'
              description={`Dot path to the array on pipeline data (default ${DEFAULT_SUPPLEMENTAL_ITEMS_PATH})`}
            >
              <SydInput size='sm' placeholder={DEFAULT_SUPPLEMENTAL_ITEMS_PATH} {...field} />
            </SydLabel>
          )}
        />
      )}
      <Controller
        name='displayConfig.header'
        control={form.control}
        render={({ field }) => (
          <SydLabel label='Section header (optional)' description='Title shown above the supplemental section in the detail panel'>
            <SydInput size='sm' placeholder='e.g. Tasks' {...field} />
          </SydLabel>
        )}
      />
      {isSmartComponent && (
        <>
          <div className='flex gap-lg'>
            <Controller
              name='displayConfig.blueprintCode'
              control={form.control}
              rules={{ validate: v => isSmartComponent && !v?.trim() ? 'Blueprint code is required' : true }}
              render={({ field, fieldState }) => (
                <SydLabel label='Blueprint code' error={hookFormErrorAdapter(form, fieldState)} required>
                  <SydInput size='sm' placeholder='e.g. wo-sf-tasks' {...field} />
                </SydLabel>
              )}
            />
            <Controller
              name='displayConfig.presentationKey'
              control={form.control}
              rules={{ validate: v => isSmartComponent && !v?.trim() ? 'Presentation key is required' : true }}
              render={({ field, fieldState }) => (
                <SydLabel label='Presentation key' error={hookFormErrorAdapter(form, fieldState)} required>
                  <SydInput size='sm' placeholder='e.g. wo-sf-task-list' {...field} />
                </SydLabel>
              )}
            />
            <Controller
              name='displayConfig.idField'
              control={form.control}
              render={({ field }) => (
                <SydLabel label='ID field (optional)' description='Field on matched rows for filterIds'>
                  <SydInput size='sm' placeholder='e.g. id' {...field} />
                </SydLabel>
              )}
            />
            <Controller
              name='displayConfig.responseItemsPath'
              control={form.control}
              render={({ field }) => (
                <SydLabel label='Response items path (optional)' description='Dot path in SE response (default: tasks)'>
                  <SydInput size='sm' placeholder='tasks' {...field} />
                </SydLabel>
              )}
            />
          </div>
          <Controller
            name='displayConfig.containerStyle'
            control={form.control}
            render={({ field }) => (
              <SydLabel label='Container style (optional)' description='JSON style object for wrapper div'>
                <SydInput size='sm' placeholder='e.g. { "margin": "-8px -12px" }' {...field} />
              </SydLabel>
            )}
          />
        </>
      )}
      {!isSmartComponent && (
        <>
          <div>
            <div className='flex gap-lg items-center'>
              <div>
                <span className='font-size-sm' style={{ fontWeight: 600 }}>Expanded detail fields</span>
                <div className='font-size-xs'>
                  Additional fields shown when a row is expanded.
                </div>
              </div>
              <SydButton type='button' size='xs' icon='add' onClick={() => appendDetailField({ field: '', label: '', format: '' })}>
                Add
              </SydButton>
            </div>
            <div className='flex flex-col gap-sm mt-2'>
              {detailFieldRows.map((row, index) => (
                <div key={row.id} className='flex items-center gap-md'>
                  <Controller
                    name={`displayConfig.detailFieldRows.${index}.field`}
                    control={form.control}
                    render={({ field }) => (
                      <SydInput {...field} size='sm' placeholder='Data key (e.g. Subject)' />
                    )}
                  />
                  <Controller
                    name={`displayConfig.detailFieldRows.${index}.label`}
                    control={form.control}
                    render={({ field }) => (
                      <SydInput {...field} size='sm' placeholder='Display label' />
                    )}
                  />
                  <Controller
                    name={`displayConfig.detailFieldRows.${index}.format`}
                    control={form.control}
                    render={({ field }) => (
                      <SydSelect {...field} options={DETAIL_FIELD_FORMAT_OPTIONS} size='sm' />
                    )}
                  />
                  <SydButton variant='primary' priority='warning' type='button' size='xs' icon='close' onClick={() => removeDetailField(index)}>
                    Remove
                  </SydButton>
                </div>
              ))}
            </div>
          </div>
          <div className={classes.group}>
            {SUPPLEMENTAL_FIELD_MAP_KEYS.map(({ key, label, placeholder, defaultField }) => (
              <Controller
                key={key}
                name={`displayConfig.fields.${key}`}
                control={form.control}
                render={({ field }) => (
                  <SydLabel label={label} description={`Default: ${defaultField}`}>
                    <SydInput size='sm' placeholder={placeholder} {...field} />
                  </SydLabel>
                )}
              />
            ))}
          </div>
          <Controller
            name='displayConfig.completedValue'
            control={form.control}
            render={({ field }) => (
              <SydLabel label='Completed status value' description='Default: Completed'>
                <SydInput size='sm' placeholder='Completed' {...field} />
              </SydLabel>
            )}
          />
        </>
      )}
      <div className={classes.group}>
        <Controller
          name='matchConfig.field'
          control={form.control}
          render={({ field }) => (
            <SydLabel label='Match Field'>
              <SydInput size='sm' placeholder='--' {...field} />
            </SydLabel>
          )}
        />
        <Controller
          name='matchConfig.op'
          control={form.control}
          render={({ field }) => (
            <SydLabel label='Match Operator'>
              <SydSelect {...field} options={SUPPLEMENTAL_MATCH_OP_OPTIONS} size='sm' />
            </SydLabel>
          )}
        />
      </div>
      <Controller
        name='matchConfig.value'
        control={form.control}
        render={({ field, fieldState }) => (
          <SydLabel
            label='Match Value'
            description={matchOp === 'in' || matchOp === 'nin' ? 'JSON array or comma-separated values' : 'Use $codeName or $statusReportItemId to compare to this line'}
            error={hookFormErrorAdapter(form, fieldState)}
            required
          >
            {matchOp === 'in' || matchOp === 'nin' ? (
              <textarea
                className='font-size-sm'
                rows={3}
                style={{ width: '100%', minWidth: '300px', padding: 'var(--dim-2)', borderRadius: 'var(--border-radius-sm)', border: 'var(--border-neutral-thin)' }}
                placeholder='["a","b"] or a, b'
                {...field}
              />
            ) : (
              <SydInput
                size='sm'
                placeholder='e.g. retire or $codeName'
                {...field}
              />
            )}
          </SydLabel>
        )}
      />

      <div className={classes.actions}>
        <SydButton type='button' variant='ghost' size='lg' onClick={onCancel}>Cancel</SydButton>
        <SydButton type='submit' variant='primary' size='lg' processing={processing}>Save</SydButton>
      </div>
    </form>
  )
}

SupplementalSourceForm.propTypes = {
  templateItem: PropTypes.object,
  mapping: PropTypes.object,
  onCancel: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired
}

export default SupplementalSourceForm
